import { playable } from "./console";

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Which lock guards this memory. Alternates by progress; falls back to a sequence when the object has no riddle
// or no dial is available to answer one.
export const pickKind = (challenges, specimen, decodedCount, hasDial = true) => {
  if (!challenges?.enabled) return null;
  const hasRiddle = Boolean(specimen?.riddle?.question) && hasDial;
  if (challenges.mode === "sequence" || !hasRiddle) return "sequence";
  if (challenges.mode === "riddle") return "riddle";
  return decodedCount % 2 === 0 ? "sequence" : "riddle";
};

export const sequenceLength = (challenges, decodedCount) => {
  const min = challenges?.min_steps ?? 3;
  const max = Math.max(min, challenges?.max_steps ?? 6);
  return clamp(min + Math.floor(decodedCount / 3), min, max);
};

// Push buttons only (no switches, dials or the confirm button), never the same control twice in a row.
export const buildSequence = (map, length, roles = [], rng = Math.random) => {
  const pool = playable(map, "button", roles).filter((c) => c.id !== "c.b9");
  if (!pool.length) return [];
  const steps = [];
  while (steps.length < length) {
    const pick = pool[Math.floor(rng() * pool.length)];
    if (pool.length > 1 && steps[steps.length - 1]?.id === pick.id) continue;
    steps.push({ id: pick.id, name: pick.name, type: pick.type, device: pick.device });
  }
  return steps;
};

export const pickDial = (map, roles = [], rng = Math.random) => {
  const dials = playable(map, "dial", roles);
  return dials[Math.floor(rng() * dials.length)] || null;
};

// Fragment riddle from this object, or - later in the run - a recall question about a memory already recovered.
export const pickRiddle = (specimen, decodedSpecimens, decodedCount, rng = Math.random) => {
  const own = specimen.riddle || {};
  const recallable = decodedSpecimens.filter((s) => s.id !== specimen.id && s.riddle?.recall);
  if (decodedCount >= 3 && recallable.length && rng() < 0.4) {
    const src = recallable[Math.floor(rng() * recallable.length)];
    return {
      mode: "recall",
      sourceTitle: src.memory?.title || src.name,
      question: src.riddle.recall,
      answer: src.riddle.recall_answer,
      hint: `It is in the memory you already recovered: "${src.memory?.title || src.name}".`,
    };
  }
  return { mode: "fragment", fragment: own.fragment, question: own.question, answer: own.answer, hint: own.hint };
};

// Rough pitch per control so each step in a sequence has its own note.
export const noteFor = (id) => {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) % 997;
  const scale = [262, 294, 330, 392, 440, 523, 587, 659, 784, 880];
  return scale[h % scale.length];
};
